// security的辅助函数
import _ from 'lodash';
import type { UserProcessStatus } from 'types/customer';
import type { AccountItemType, ApiWebUserDataType } from 'types/user';
import { getQueryValue } from 'utils/storage';
import APPStore from './APP';
import security from './security';

export type QueryAccountType = 'ib' | 'bs';

// status的权重(仅在app内打开, 且app切换至虚拟账户时有用)
export const statusCodeMap: Record<UserProcessStatus, number> = {
    UNACCOUNT: 0,
    CONTINUE: 1,
    INVALID: 1,
    AUDITING: 2,
    FACE: 2,
    CLOSED: 2,
    ACTIVATING: 2,
    OPENED: 3,
    FUNDED: 4
};

/**
 * @description 判断并选择当前的账户
 *      如果链接上有customer_id参数, 就使用该customer_id对应的账户作为当前账户
 *      否则, 如果链接上有account参数, 就是用链接上的account参数判断(不推荐)当前账户
 *      否则, 如果存在currentCustomerId, 就从accountList中找对应的账户信息
 *          如果找到了, 就是使用当前账户信息; 如果没找到(没找到应该是服务端数据有错, 所以这里只做兼容处理), 就使用accountList中的第一个账户信息
 *      如果不存在currentCustomerId, 有两种可能:
 *          1. 当前账户切换到了虚拟账户(个人中心不支持切换至虚拟账户)
 *          2. 当前用户新注册, 没走开户流程, 所以没有currentCustomerId
 *          对于第1种情况: 判断当前accountList下的用户的状态, 按照优先级使用状态更靠前的
 *          对于第2种情况: 同第1中情况的处理方式(会有一个只有status的数据)
 * @param {ApiWebUserDataType} apiWebUserData
 */
export function chooseCurrentAccount(
    { actualAccountInfo: { accountList: fullAccountList, currentCustomerId } }: ApiWebUserDataType,
    queryAccount: QueryAccountType
): AccountItemType {
    // 此项目在tigertrade中被接入，需要过滤掉非Marsco牌照数据
    const accountList = APPStore.isTigerTrade
        ? fullAccountList.filter(account => !account.license || account.license === 'Marsco')
        : fullAccountList;
    const resultCurrentAccount: AccountItemType =
        (currentCustomerId && accountList.find(account => account.customerId === currentCustomerId)) ||
        _.orderBy(accountList, item => statusCodeMap[item.status], 'desc')[0];
    // APP内，如果链接中指定了customer_id, 此时需要根据连接上的customer_id来判断当前账户
    // 具体使用场景可以询问对应产品 @柯楠/@李睿, 该参数谨慎使用
    const queryCustomerId = getQueryValue('customer_id');

    if (queryCustomerId) {
        // 这里需要考虑假如accountList里不存在url上的customerId对应的账户信息的情况
        return accountList.find(account => account.customerId === Number(queryCustomerId)) || resultCurrentAccount;
        // 此处理方式仅为兼容老链接, 不推荐使用account=xx形式的链接
    } else if (queryAccount === 'bs') {
        const bsAccountList = accountList.filter(item => item.accountType === 'bs');

        if (bsAccountList.length === 1) {
            return bsAccountList[0];
        }
    } else if (queryAccount === 'ib') {
        const ibAccountList = accountList.filter(
            ({ accountType }) => accountType && ['nd', 'fd'].includes(accountType)
        );

        if (ibAccountList.length === 1) {
            return ibAccountList[0];
        }
    }

    return resultCurrentAccount;
}

/**
 * 判断是否有 IRA 账户和综合账户，只有同时有的时候才能进入 /deposit/transfer 页面
 */
export function hasIRAAccountAndLiveAccount(): boolean {
    const { accountList = [] } = security.currentUser?.actualAccountInfo || {};

    return (
        accountList.some(item => item.category === 'IRA') && accountList.some(item => item.category === 'Individual')
    );
}
